import { functionalityStore } from '@/components/Functionality';
import { FunctionalityUnit }  from '@/components/Functionality/engine/types';
import { toLimit }            from '@/components/Functionality/engine/types';

/**
 * Mise en mots des fonctionnalités. Ce que chacune compte est déclaré dans le dictionnaire du
 * bundle ; il ne reste ici que la façon de l'écrire en français.
 */

/** Périmètre d'une limite, tel qu'on l'écrit à la suite de l'unité : « 500 lieux par projet ». */
const SCOPE_LABELS: { label: string; scope: string }[] = [
	{ label: 'par projet', scope: 'project' },
];

/** Ordre d'affichage des limites : d'abord le compte, puis les périmètres de plus en plus fins. */
export const LIMIT_SCOPES_ORDER = SCOPE_LABELS.map(({ scope }) => scope);

const scopeLabelOf = (reference: Account_FunctionalityReference) => (
	SCOPE_LABELS.find(({ scope }) => scope === functionalityStore.scopeOf(reference))?.label
);

const isUnlimited = (count: number | string) => toLimit(count) === Infinity;

const startsWithVowel = (word: string) => /^[aeiouyàâéèêîïôûù]/i.test(word);

export const partitive = (word: string) => `${startsWithVowel(word) ? 'd\'' : 'de '}${word}`;

export const limitCount = (count: number | string) => (isUnlimited(count) ? 'illimité' : String(count));

export const countedUnit = (count: number | string, unit: FunctionalityUnit) => (
	isUnlimited(count)
		? `Nombre illimité ${partitive(unit.many)}`
		: `${count} ${Number(count) > 1 ? unit.many : unit.one}`
);

export const counted = (count: number, word: string) => `${count} ${word}${count > 1 ? 's' : ''}`;

/** L'unité d'une limite, suffixée de son périmètre. À défaut d'unité déclarée, son libellé. */
export const limitUnit = (reference: Account_FunctionalityReference, fallbackTitle?: string): FunctionalityUnit => {
	const unit = functionalityStore.unitOf(reference);

	if (!unit) {
		const label = fallbackTitle || reference;

		return { many: label, one: label };
	}

	const scopeLabel = scopeLabelOf(reference);

	if (!scopeLabel) return unit;

	return { many: `${unit.many} ${scopeLabel}`, one: `${unit.one} ${scopeLabel}` };
};

/** Ce qu'on lit dans une liste : « Statistiques avancées », « 10 projets ». */
export const functionalityLabel = (
	reference: Account_FunctionalityReference,
	value: string,
	fallbackTitle?: string,
) => {
	if (functionalityStore.isFeature(reference) || !functionalityStore.unitOf(reference)) {
		return fallbackTitle || reference;
	}

	return countedUnit(value, limitUnit(reference, fallbackTitle));
};
